// src/ActivityLog.js

import React, { useState, useEffect, useMemo } from 'react';
import { db } from './firebase';
import { collection, query, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { useUser } from './UserContext';
import moment from 'moment';
import './ActivityLog.css';

function ActivityLog() {
    const { userProfile } = useUser();
    const [logs, setLogs] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    // Stati per i filtri
    const [filterDateStart, setFilterDateStart] = useState('');
    const [filterDateEnd, setFilterDateEnd] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        if (!userProfile || !userProfile.centerId) return;

        const q = query(
            collection(db, 'centers', userProfile.centerId, 'logs'),
            orderBy('timestamp', 'desc'),
            limit(500)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const logsData = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setLogs(logsData);
            setIsLoading(false);
        }, (error) => {
            console.error("Errore recupero logs:", error);
            setIsLoading(false);
        });

        return () => unsubscribe();
    }, [userProfile]);

    // Logica di filtraggio client-side
    const filteredLogs = useMemo(() => {
        return logs.filter(log => {
            const logDate = log.timestamp?.toDate ? moment(log.timestamp.toDate()) : moment(log.timestamp);
            
            // 1. Filtro Data Inizio
            if (filterDateStart && logDate.isBefore(moment(filterDateStart).startOf('day'))) {
                return false;
            }

            // 2. Filtro Data Fine
            if (filterDateEnd && logDate.isAfter(moment(filterDateEnd).endOf('day'))) {
                return false;
            }

            // 3. Filtro Testo
            if (searchTerm) {
                const searchLower = searchTerm.toLowerCase();
                
                // --- CORREZIONE QUI: Usiamo 'userName' invece di 'operatorName' ---
                const operator = String(log.userName || log.operatorName || '').toLowerCase();
                const action = String(log.action || '').toLowerCase();
                
                const details = typeof log.details === 'object' && log.details !== null 
                    ? JSON.stringify(log.details).toLowerCase() 
                    : String(log.details || '').toLowerCase();

                if (!operator.includes(searchLower) && !action.includes(searchLower) && !details.includes(searchLower)) {
                    return false;
                }
            }

            return true;
        });
    }, [logs, filterDateStart, filterDateEnd, searchTerm]);

    if (isLoading) return <div className="loading-spinner">Caricamento Log...</div>;

    return (
        <div className="activity-log-page">
            <div className="activity-log-header">
                <h1>Registro Attività</h1>
                <p>Monitoraggio delle operazioni svolte nel sistema.</p>
            </div>

            {/* Barra dei Filtri */}
            <div className="filters-bar">
                <div className="filter-group">
                    <label>Dal:</label>
                    <input 
                        type="date" 
                        value={filterDateStart} 
                        onChange={(e) => setFilterDateStart(e.target.value)} 
                    />
                </div>
                <div className="filter-group">
                    <label>Al:</label>
                    <input 
                        type="date" 
                        value={filterDateEnd} 
                        onChange={(e) => setFilterDateEnd(e.target.value)} 
                    />
                </div>
                <div className="filter-group search-group">
                    <label>Cerca:</label>
                    <input 
                        type="text" 
                        placeholder="Operatore, azione..." 
                        value={searchTerm} 
                        onChange={(e) => setSearchTerm(e.target.value)} 
                    />
                </div>
                <button 
                    className="reset-filters-btn"
                    onClick={() => { setFilterDateStart(''); setFilterDateEnd(''); setSearchTerm(''); }}
                >
                    Resetta
                </button>
            </div>

            {/* Tabella Log */}
            <div className="logs-table-container">
                <table className="logs-table">
                    <thead>
                        <tr>
                            <th style={{width: '180px'}}>Data e Ora</th>
                            <th style={{width: '200px'}}>Operatore</th>
                            <th>Azione</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredLogs.length > 0 ? (
                            filteredLogs.map(log => {
                                const dateObj = log.timestamp?.toDate ? log.timestamp.toDate() : new Date(log.timestamp);
                                return (
                                    <tr key={log.id}>
                                        <td className="log-date">
                                            {moment(dateObj).format('DD/MM/YYYY HH:mm')}
                                        </td>
                                        <td className="log-operator">
                                            {/* --- CORREZIONE QUI: Visualizza userName --- */}
                                            <strong>{log.userName || log.operatorName || 'Sistema'}</strong>
                                        </td>
                                        <td className="log-action">
                                            {log.action}
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan="3" className="no-logs">Nessuna attività trovata con i filtri correnti.</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            
            <div className="logs-footer">
                Visualizzati {filteredLogs.length} record (ultimi 500 caricati)
            </div>
        </div>
    );
}

export default ActivityLog;